import type { PrV2DiscussionResponse, PrV2ReactionContent } from '@/lib/pr-v2/types';
import type { Reactions } from '@/types/github';

type RollbackSnapshot = {
  reaction: Reactions[number];
  index: number;
};

type UpdateOptimisticReactionsArgs = {
  reactions: Record<number, Reactions>;
  commentId: number;
  emoji: PrV2ReactionContent;
  action: 'create' | 'delete';
  reactionId?: number;
  optimisticReactionId?: number;
  userGithubId: number;
  userLogin: string;
  userAvatarUrl: string;
};

/**
 * Builds a synthetic reaction for optimistic updates prior to server confirmation.
 */
export function buildOptimisticReaction(
  reactionId: number,
  emoji: PrV2ReactionContent,
  userGithubId: number,
  login: string,
  avatarUrl: string,
): Reactions[number] {
  return {
    id: reactionId,
    node_id: '',
    content: emoji,
    user: {
      login,
      id: userGithubId,
      node_id: '',
      avatar_url: avatarUrl,
      gravatar_id: null,
      url: '',
      html_url: '',
      followers_url: '',
      following_url: '',
      gists_url: '',
      starred_url: '',
      subscriptions_url: '',
      organizations_url: '',
      repos_url: '',
      events_url: '',
      received_events_url: '',
      type: 'User',
      site_admin: false,
    },
  } as Reactions[number];
}

export function getCommentReactions(
  discussion: PrV2DiscussionResponse | undefined,
  commentId: number,
): Reactions {
  return discussion?.reactions?.[commentId] ?? [];
}

export function getUserReactionFromDiscussion(args: {
  discussion: PrV2DiscussionResponse | undefined;
  commentId: number;
  userGithubId: number;
  emoji: PrV2ReactionContent;
}): Reactions[number] | null {
  return (
    getCommentReactions(args.discussion, args.commentId).find(
      (reaction) => reaction.user?.id === args.userGithubId && reaction.content === args.emoji,
    ) ?? null
  );
}

export function getReactionCountFromDiscussion(args: {
  discussion: PrV2DiscussionResponse | undefined;
  commentId: number;
  emoji: PrV2ReactionContent;
}): number {
  return getCommentReactions(args.discussion, args.commentId).filter(
    (reaction) => reaction.content === args.emoji,
  ).length;
}

export function getReactionUsersFromDiscussion(args: {
  discussion: PrV2DiscussionResponse | undefined;
  commentId: number;
  emoji: PrV2ReactionContent;
}): string[] {
  const seenLogins = new Set<string>();

  return getCommentReactions(args.discussion, args.commentId).reduce<string[]>(
    (users, reaction) => {
      const login =
        reaction.content === args.emoji && reaction.user?.login ? reaction.user.login.trim() : '';
      if (!login || seenLogins.has(login)) {
        return users;
      }

      seenLogins.add(login);
      users.push(login);
      return users;
    },
    [],
  );
}

export function updateOptimisticReactions(
  args: UpdateOptimisticReactionsArgs,
): Record<number, Reactions> {
  const existing = args.reactions[args.commentId] ?? [];

  if (args.action === 'create') {
    if (args.optimisticReactionId == null) {
      return args.reactions;
    }

    const alreadyExists = existing.some(
      (reaction) => reaction.user?.id === args.userGithubId && reaction.content === args.emoji,
    );

    if (alreadyExists) {
      return args.reactions;
    }

    return {
      ...args.reactions,
      [args.commentId]: [
        ...existing,
        buildOptimisticReaction(
          args.optimisticReactionId,
          args.emoji,
          args.userGithubId,
          args.userLogin,
          args.userAvatarUrl,
        ),
      ],
    };
  }

  return {
    ...args.reactions,
    [args.commentId]: existing.filter((reaction) => {
      if (args.reactionId && reaction.id === args.reactionId) {
        return false;
      }

      return !(reaction.user?.id === args.userGithubId && reaction.content === args.emoji);
    }),
  };
}

export function getReactionRollbackSnapshotFromDiscussion(args: {
  discussion: PrV2DiscussionResponse | undefined;
  commentId: number;
  reactionId?: number;
  userGithubId: number;
  emoji: PrV2ReactionContent;
}): RollbackSnapshot | null {
  const existing = getCommentReactions(args.discussion, args.commentId);
  const reactionIndex = existing.findIndex((reaction) => {
    if (args.reactionId && reaction.id === args.reactionId) {
      return true;
    }

    return reaction.user?.id === args.userGithubId && reaction.content === args.emoji;
  });

  if (reactionIndex < 0) {
    return null;
  }

  const reaction = existing[reactionIndex];
  if (!reaction) {
    return null;
  }

  return {
    reaction,
    index: reactionIndex,
  };
}

export function materializeCanonicalReaction(args: {
  reactions: Record<number, Reactions>;
  commentId: number;
  canonicalReactionId: number;
  emoji: PrV2ReactionContent;
  userGithubId: number;
  userLogin: string;
  userAvatarUrl: string;
}): Record<number, Reactions> {
  const existing = args.reactions[args.commentId] ?? [];
  const nextReactions: Reactions = [];
  let insertedCanonical = false;

  for (const reaction of existing) {
    const isTargetReaction =
      reaction.user?.id === args.userGithubId && reaction.content === args.emoji;

    if (!isTargetReaction) {
      nextReactions.push(reaction);
      continue;
    }

    if (!insertedCanonical) {
      nextReactions.push({
        ...reaction,
        id: args.canonicalReactionId,
      });
      insertedCanonical = true;
    }
  }

  if (!insertedCanonical) {
    nextReactions.push(
      buildOptimisticReaction(
        args.canonicalReactionId,
        args.emoji,
        args.userGithubId,
        args.userLogin,
        args.userAvatarUrl,
      ),
    );
  }

  return {
    ...args.reactions,
    [args.commentId]: nextReactions,
  };
}

export function removeReactionById(args: {
  reactions: Record<number, Reactions>;
  commentId: number;
  reactionId: number;
}): Record<number, Reactions> {
  const existing = args.reactions[args.commentId] ?? [];

  return {
    ...args.reactions,
    [args.commentId]: existing.filter((reaction) => reaction.id !== args.reactionId),
  };
}

export function restoreDeletedReaction(args: {
  reactions: Record<number, Reactions>;
  commentId: number;
  reaction: Reactions[number];
  index: number;
}): Record<number, Reactions> {
  const existing = args.reactions[args.commentId] ?? [];
  if (existing.some((candidate) => candidate.id === args.reaction.id)) {
    return args.reactions;
  }

  const nextReactions = [...existing];
  const insertAt = Math.max(0, Math.min(args.index, nextReactions.length));
  nextReactions.splice(insertAt, 0, args.reaction);

  return {
    ...args.reactions,
    [args.commentId]: nextReactions,
  };
}

export function applyReactionBucketOptimisticState(args: {
  discussion: PrV2DiscussionResponse;
  reactions: Record<number, Reactions>;
  commentId: number;
}): PrV2DiscussionResponse {
  return {
    ...args.discussion,
    reactions: args.reactions,
    optimisticReactionBuckets: {
      ...(args.discussion.optimisticReactionBuckets ?? {}),
      [args.commentId]: {
        updatedAtMs: Date.now(),
        reactions: args.reactions[args.commentId] ?? ([] as Reactions),
      },
    },
  };
}
